import type {
  CompleteRoundAction,
  GameState,
  LockVoteAction,
  ResolveVoteAction,
  RevealCardAction,
  SetDiscussionDurationAction,
  StartNextRoundAction,
  StartVoteAction,
  TickDiscussionAction,
  TickVoteAction,
  ValidationResult,
} from "../types";
import { validateHostAction } from "./common";

const MIN_DISCUSSION_DURATION_SECONDS = 1;
const MAX_DISCUSSION_DURATION_SECONDS = 1800;

function hasPlayer(state: GameState, playerId: string): boolean {
  return state.players.some((player) => player.id === playerId);
}

function currentCardIdAtPlayer(
  state: GameState,
  playerId: string,
): string | null {
  const entry = Object.entries(state.cardPositions).find(
    ([, position]) =>
      position.zone === "player" && position.playerId === playerId,
  );
  return entry?.[0] ?? null;
}

function cardIdAtRevealTarget(
  state: GameState,
  target: RevealCardAction["target"],
): string | null {
  if (target.kind === "center") {
    return state.centerSlots[target.slotIndex] ?? null;
  }
  return currentCardIdAtPlayer(state, target.playerId);
}

export function validateStartVote(
  state: GameState,
  action: StartVoteAction,
): ValidationResult {
  const hostCheck = validateHostAction(
    state,
    action.actorPlayerId,
    "Only host can start vote",
  );
  if (!hostCheck.valid) return hostCheck;

  if (state.phase !== "day") {
    return { valid: false, error: "Vote can only start during day" };
  }
  if (
    (state.discussionRemainingSeconds ?? state.discussionDurationSeconds) > 0
  ) {
    return {
      valid: false,
      error: "Vote window can only open after discussion reaches zero",
    };
  }
  return { valid: true };
}

export function validateSetDiscussionDuration(
  state: GameState,
  action: SetDiscussionDurationAction,
): ValidationResult {
  const hostCheck = validateHostAction(
    state,
    action.actorPlayerId,
    "Only host can set discussion duration",
  );
  if (!hostCheck.valid) return hostCheck;

  if (state.phase !== "setup") {
    return {
      valid: false,
      error: "Discussion duration can only be set during setup",
    };
  }
  if (
    !Number.isInteger(action.durationSeconds) ||
    action.durationSeconds < MIN_DISCUSSION_DURATION_SECONDS ||
    action.durationSeconds > MAX_DISCUSSION_DURATION_SECONDS
  ) {
    return {
      valid: false,
      error: "Discussion duration must be between 1 and 1800 seconds",
    };
  }
  return { valid: true };
}

export function validateTickDiscussion(
  state: GameState,
  action: TickDiscussionAction,
): ValidationResult {
  const hostCheck = validateHostAction(
    state,
    action.actorPlayerId,
    "Only host can tick discussion",
  );
  if (!hostCheck.valid) return hostCheck;

  if (state.phase !== "day") {
    return { valid: false, error: "Discussion can only tick during day" };
  }
  if (!Number.isInteger(action.elapsedSeconds) || action.elapsedSeconds < 1) {
    return { valid: false, error: "Elapsed seconds must be positive" };
  }
  return { valid: true };
}

export function validateLockVote(
  state: GameState,
  action: LockVoteAction,
): ValidationResult {
  if (state.phase !== "vote") {
    return {
      valid: false,
      error: "Votes can only be cast during the vote window",
    };
  }
  if (!hasPlayer(state, action.actorPlayerId)) {
    return { valid: false, error: "Voting player does not exist" };
  }
  if (!hasPlayer(state, action.targetPlayerId)) {
    return { valid: false, error: "Vote target does not exist" };
  }
  if (action.actorPlayerId === action.targetPlayerId) {
    return { valid: false, error: "Players cannot vote for themselves" };
  }

  const player = state.players.find(
    (candidate) => candidate.id === action.actorPlayerId,
  );
  if (player?.voteTargetId) {
    return { valid: false, error: "Vote is already locked" };
  }
  return { valid: true };
}

export function validateTickVote(
  state: GameState,
  action: TickVoteAction,
): ValidationResult {
  const hostCheck = validateHostAction(
    state,
    action.actorPlayerId,
    "Only host can tick vote",
  );
  if (!hostCheck.valid) return hostCheck;

  if (state.phase !== "vote") {
    return { valid: false, error: "Vote can only tick during vote" };
  }
  if (!Number.isInteger(action.elapsedSeconds) || action.elapsedSeconds < 1) {
    return { valid: false, error: "Elapsed seconds must be positive" };
  }
  return { valid: true };
}

export function validateResolveVote(
  state: GameState,
  action: ResolveVoteAction,
): ValidationResult {
  const hostCheck = validateHostAction(
    state,
    action.actorPlayerId,
    "Only host can resolve votes",
  );
  if (!hostCheck.valid) return hostCheck;

  if (state.phase !== "vote") {
    return { valid: false, error: "Votes can only be resolved during vote" };
  }
  const voteTimedOut = state.voteRemainingSeconds === 0;
  if (!voteTimedOut && state.players.some((player) => !player.voteTargetId)) {
    return {
      valid: false,
      error: "All players must lock votes before resolving",
    };
  }
  return { valid: true };
}

export function validateRevealCard(
  state: GameState,
  action: RevealCardAction,
): ValidationResult {
  const hostCheck = validateHostAction(
    state,
    action.actorPlayerId,
    "Only host can reveal cards",
  );
  if (!hostCheck.valid) return hostCheck;

  if (state.phase !== "reveal" && state.phase !== "complete") {
    return { valid: false, error: "Cards can only be revealed during reveal" };
  }
  if (!state.voteResult) {
    return { valid: false, error: "Vote result is required before reveal" };
  }

  if (
    action.target.kind === "player" &&
    !hasPlayer(state, action.target.playerId)
  ) {
    return { valid: false, error: "Reveal target player does not exist" };
  }

  const cardId = cardIdAtRevealTarget(state, action.target);
  if (!cardId) {
    return { valid: false, error: "Reveal target card does not exist" };
  }
  if (state.revealedCardIds.includes(cardId)) {
    return { valid: false, error: "Card is already revealed" };
  }
  return { valid: true };
}

export function validateCompleteRound(
  state: GameState,
  action: CompleteRoundAction,
): ValidationResult {
  const hostCheck = validateHostAction(
    state,
    action.actorPlayerId,
    "Only host can complete round",
  );
  if (!hostCheck.valid) return hostCheck;

  if (state.phase !== "reveal") {
    return { valid: false, error: "Round can only be completed during reveal" };
  }
  if (!state.voteResult || !state.gameOutcome) {
    return {
      valid: false,
      error: "Vote result is required before round completion",
    };
  }
  return { valid: true };
}

export function validateStartNextRound(
  state: GameState,
  action: StartNextRoundAction,
): ValidationResult {
  const hostCheck = validateHostAction(
    state,
    action.actorPlayerId,
    "Only host can start next round",
  );
  if (!hostCheck.valid) return hostCheck;

  if (state.phase !== "complete") {
    return {
      valid: false,
      error: "Next round can only start after round completion",
    };
  }
  return { valid: true };
}
